import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

type AgencyRole = 'design' | 'shopify_ops' | 'ads_ops'

interface MkAgency {
  id: number
  slug: string
  name: string
  email: string
  role: AgencyRole
  active: boolean
  createdAt: string
  updatedAt: string
}

const ROLE_LABEL: Record<AgencyRole, string> = {
  design: 'Design (bannere)',
  shopify_ops: 'Shopify Ops (site + checkout)',
  ads_ops: 'Ads Ops (Google + Meta Ads)',
}

const ROLE_DESCRIPTION: Record<AgencyRole, string> = {
  design:
    'Face cele 9 bannere standard (hero, popup, ads). Primește brief-ul campaniei și urcă fișierele în Storage Share. Furnizor curent: 360 Advertising.',
  shopify_ops:
    'Instalează bannerele pe site Shopify (homepage, popup, sidebar) + activează voucher la checkout sau setează preț tăiat. Furnizor curent: WebBlast.',
  ads_ops:
    'Pornește campaniile plătite Google Ads + Meta Ads cu bugetul aprobat și trimite raportul final (comenzi, vânzări, ROAS). Furnizor curent: DoAds.',
}

const DEFAULT_AGENCIES: Array<{
  slug: string
  name: string
  email: string
  role: AgencyRole
}> = [
  {
    slug: '360-advertising',
    name: '360 Advertising',
    email: 'contact@360advertising.ro',
    role: 'design',
  },
  { slug: 'webblast', name: 'WebBlast', email: 'contact@webblast.ro', role: 'shopify_ops' },
  { slug: 'doads', name: 'DoAds', email: 'contact@doads.ro', role: 'ads_ops' },
]

export function SetariAgentiiMarketingPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.campaigns_mk:write')

  const [editTarget, setEditTarget] = useState<MkAgency | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const query = useQuery<{ agencies: MkAgency[] }>({
    queryKey: ['mk-agencies'],
    queryFn: async () => (await api.get<{ agencies: MkAgency[] }>('/marketing/mk-agencies')).data,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/mk-agencies/${id}`)).data,
    onSuccess: () => {
      toast.success('Agenție ștearsă')
      queryClient.invalidateQueries({ queryKey: ['mk-agencies'] })
    },
    onError: showError,
  })

  const seedMutation = useMutation({
    mutationFn: async () => {
      const existingRoles = new Set(query.data?.agencies.map((a) => a.role) ?? [])
      const toCreate = DEFAULT_AGENCIES.filter((a) => !existingRoles.has(a.role))
      for (const a of toCreate) {
        await api.post('/marketing/mk-agencies', a)
      }
      return toCreate.length
    },
    onSuccess: (count) => {
      toast.success(
        count > 0
          ? `${count} agenți${count === 1 ? 'e adăugată' : 'i adăugate'} cu valori implicite — editează emailurile.`
          : 'Toate cele 3 roluri sunt deja configurate.',
      )
      queryClient.invalidateQueries({ queryKey: ['mk-agencies'] })
    },
    onError: showError,
  })

  const agencies = query.data?.agencies ?? []
  const missingRoles = (['design', 'shopify_ops', 'ads_ops'] as AgencyRole[]).filter(
    (r) => !agencies.some((a) => a.role === r && a.active),
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Setări → Agenții marketing</h1>
        {canWrite && (
          <div className="flex gap-2">
            {agencies.length === 0 && (
              <Button variant="secondary" onClick={() => seedMutation.mutate()}>
                + Adaugă cele 3 default
              </Button>
            )}
            <Button onClick={() => setCreateOpen(true)}>+ Agenție nouă</Button>
          </div>
        )}
      </div>

      {missingRoles.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Lipsesc agenții pentru:</strong>{' '}
          {missingRoles.map((r) => ROLE_LABEL[r]).join(' · ')}.
          <div className="mt-1 text-xs">
            Fără ele, butoanele &laquo;Trimite email&raquo; din Campanii marketing returnează eroare
            400.
          </div>
        </div>
      )}

      <Card description="Agențiile externe către care CRM-ul compune și trimite emailurile campaniilor. Fiecare rol (design / shopify_ops / ads_ops) trebuie să aibă cel puțin o agenție activă pentru ca fluxul Campanii marketing să funcționeze.">
        {query.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {agencies.length === 0 && !query.isLoading && (
          <p className="text-sm text-b4l-text-muted">
            Niciun rol configurat. Apasă &laquo;Adaugă cele 3 default&raquo; pentru valori inițiale
            (le poți edita ulterior).
          </p>
        )}
        {agencies.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                  <th className="px-2 py-2">Rol</th>
                  <th className="px-2 py-2">Nume</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Cod intern</th>
                  <th className="px-2 py-2">Stare</th>
                  {canWrite && <th className="px-2 py-2">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {agencies.map((a) => (
                  <tr key={a.id} className="border-b border-b4l-border hover:bg-b4l-bg-soft">
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{ROLE_LABEL[a.role]}</div>
                      <div className="text-[10px] text-b4l-text-muted">
                        {ROLE_DESCRIPTION[a.role]}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 font-medium">{a.name}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">
                      <a href={`mailto:${a.email}`} className="text-b4l-blue-dark hover:underline">
                        {a.email}
                      </a>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-b4l-text-muted">
                      {a.slug}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          a.active
                            ? 'bg-b4l-green-light text-b4l-green-dark'
                            : 'bg-b4l-bg text-b4l-text-muted'
                        }`}
                      >
                        {a.active ? 'Activ' : 'Inactiv'}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1">
                          <Button variant="secondary" onClick={() => setEditTarget(a)}>
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Șterge agenția "${a.name}"?`)) {
                                deleteMutation.mutate(a.id)
                              }
                            }}
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createOpen && <AgencyFormModal onClose={() => setCreateOpen(false)} />}
      {editTarget && <AgencyFormModal agency={editTarget} onClose={() => setEditTarget(null)} />}
    </div>
  )
}

function AgencyFormModal({ agency, onClose }: { agency?: MkAgency; onClose: () => void }) {
  const queryClient = useQueryClient()
  const isEdit = !!agency

  const [slug, setSlug] = useState(agency?.slug ?? '')
  const [name, setName] = useState(agency?.name ?? '')
  const [email, setEmail] = useState(agency?.email ?? '')
  const [role, setRole] = useState<AgencyRole>(agency?.role ?? 'design')
  const [active, setActive] = useState(agency?.active ?? true)

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        slug: slug.trim(),
        name: name.trim(),
        email: email.trim(),
        role,
        active,
      }
      if (isEdit) {
        return (await api.patch(`/marketing/mk-agencies/${agency!.id}`, body)).data
      }
      return (await api.post('/marketing/mk-agencies', body)).data
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Agenție actualizată' : 'Agenție adăugată')
      queryClient.invalidateQueries({ queryKey: ['mk-agencies'] })
      onClose()
    },
    onError: showError,
  })

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? `Editează: ${agency?.name}` : 'Agenție nouă'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!slug.trim() || !name.trim() || !email.trim()}
          >
            {isEdit ? 'Salvează' : 'Adaugă'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-b4l-text-dim">Rol</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AgencyRole)}
            className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
          >
            <option value="design">{ROLE_LABEL.design}</option>
            <option value="shopify_ops">{ROLE_LABEL.shopify_ops}</option>
            <option value="ads_ops">{ROLE_LABEL.ads_ops}</option>
          </select>
          <p className="mt-1 text-[10px] text-b4l-text-muted">{ROLE_DESCRIPTION[role]}</p>
          <p className="mt-1 text-[10px] italic text-b4l-text-muted">
            Un rol poate avea mai multe agenții. CRM o folosește pe prima activă (ordine id
            crescător) — utile dacă vrei backup sau dacă schimbi furnizorul.
          </p>
        </div>
        <Field
          label="Nume agenție"
          placeholder="ex: 360 Advertising"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Field
          label="Email contact"
          type="email"
          placeholder="ex: contact@360advertising.ro"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <label className="block text-xs font-medium text-b4l-text-dim">
            Cod intern (doar litere mici, cifre și cratimă)
          </label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="ex: 360-advertising"
            className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 font-mono text-sm"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>Activă (CRM o folosește în Campanii marketing)</span>
        </label>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
